/**
 * Knapp för att dela kalkylen som en länk.
 *
 * Fungerar för alla datakällor:
 *   - Wizard: komprimerar wizardInput + scenario i URL:en (?s=...)
 *   - Tibber/CSV: skapar server-snapshot och delar ?delning=SLUG
 */

import { useState } from "react";
import type { UserData } from "../types";
import type { Scenario } from "../sim/types";
import { encodeWizardShareLink, createSnapshot } from "./shareLink";

interface ShareLinkButtonProps {
  userData: UserData;
  scenario: Scenario;
}

export default function ShareLinkButton({
  userData,
  scenario,
}: ShareLinkButtonProps) {
  const [copied, setCopied] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isWizard = !!userData.metadata.wizardInput;

  async function buildUrl(): Promise<string> {
    if (isWizard) {
      // Wizard-läge: allt får plats i URL:en
      const encoded = encodeWizardShareLink({
        wizardInput: userData.metadata.wizardInput!,
        scenario: {
          battery: scenario.battery,
          strategy: scenario.strategy,
          grid: scenario.grid,
        },
      });
      return `${window.location.origin}/?s=${encoded}`;
    }
    // Snapshot-läge: spara serverside, dela slug
    const slug = await createSnapshot(userData, scenario);
    return `${window.location.origin}/?delning=${slug}`;
  }

  async function handleShare() {
    setBusy(true);
    setError(null);
    try {
      const url = await buildUrl();

      if (navigator.share) {
        try {
          await navigator.share({
            title: "Batterikalkyl",
            text: "Min batterikalkyl",
            url,
          });
          return;
        } catch (err) {
          if ((err as Error).name === "AbortError") return;
        }
      }

      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunde inte skapa länk");
      setTimeout(() => setError(null), 4000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="share-link">
      <button
        type="button"
        className="share-link-button"
        onClick={handleShare}
        disabled={busy}
      >
        {busy
          ? "Skapar länk…"
          : copied
            ? "Länk kopierad!"
            : "Dela denna kalkyl"}
      </button>
      <p className="share-link-help">
        Delar din uträkning och dina antaganden. Ingen e-post, adress eller
        identifierande information ingår.
      </p>
      {error && <p className="error">{error}</p>}
    </div>
  );
}
